import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { moduleConfigAPI } from '../services/api';
import type { ModuleConfig } from '../types';
import { Switch } from '../components/ui/Switch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import toast from '../lib/toast';
import { BackHeader } from '../components/BackHeader';
import { 
  DollarSign, 
  Building2, 
  Calendar, 
  MessageSquare, 
  Vote, 
  QrCode, 
  AlertTriangle, 
  Users, 
  Wrench,
  ShoppingBag,
  Home,
  Settings,
  Loader2,
  AlertCircle
} from 'lucide-react';

// P1-B (2026-09-08): exported so CondoAdminPanel (/admin/condo) can reuse the
// same module taxonomy + save contract (POST /condos/:id/modules).
export const MODULE_CATEGORIES = {
  finanzas: {
    name: 'Finanzas',
    icon: DollarSign,
    color: 'bg-green-500',
    modules: [
      { id: 'finanzas', name: 'Dashboard Financiero', description: 'Resumen de ingresos y gastos' },
      { id: 'ingresos', name: 'Ingresos', description: 'Registrar y gestionar ingresos' },
      { id: 'gastos', name: 'Gastos', description: 'Registrar y gestionar gastos' },
      { id: 'cotizaciones', name: 'Cotizaciones', description: 'Proyectos y cotizaciones' },
      { id: 'pagos', name: 'Pagos', description: 'Gestión de pagos de residentes' },
      { id: 'morosos', name: 'Morosos', description: 'Seguimiento de cuentas pendientes' },
    ]
  },
  amenidades: {
    name: 'Amenidades',
    icon: Building2,
    color: 'bg-blue-500',
    modules: [
      { id: 'amenidades', name: 'Reservas de Amenidades', description: 'Piscina, gym, salón de eventos' },
    ]
  },
  comunicacion: {
    name: 'Comunicación',
    icon: MessageSquare,
    color: 'bg-purple-500',
    modules: [
      { id: 'calendario', name: 'Calendario', description: 'Eventos y actividades' },
      { id: 'quejas', name: 'Quejas y Sugerencias', description: 'Buzón de sugerencias' },
      { id: 'anuncios', name: 'Anuncios', description: 'Comunicados oficiales' },
      { id: 'votaciones', name: 'Votaciones', description: 'Encuestas y votaciones' },
    ]
  },
  seguridad: {
    name: 'Seguridad',
    icon: AlertTriangle,
    color: 'bg-red-500',
    modules: [
      { id: 'qr_acceso', name: 'QR y Acceso', description: 'Códigos QR de acceso' },
      { id: 'panic_button', name: 'Botón de Pánico', description: 'Emergencias SOS' },
      { id: 'visitantes', name: 'Registro de Visitantes', description: 'Control de acceso' },
      { id: 'emergencias', name: 'Emergencias', description: 'Contactos de emergencia' },
    ]
  },
  home: {
    name: 'Home+ (Residentes)',
    icon: Home,
    color: 'bg-cyan-500',
    modules: [
      { id: 'home_tareas', name: 'Tareas', description: 'Gestión de tareas del hogar' },
      { id: 'home_compras', name: 'Lista de Compras', description: 'Listas de compras' },
      { id: 'home_mascotas', name: 'Mascotas', description: 'Perfiles de mascotas' },
      { id: 'home_vehiculos', name: 'Vehículos', description: 'Mantenimiento de vehículos' },
      { id: 'home_presupuesto', name: 'Presupuesto', description: 'Presupuesto familiar' },
    ]
  },
  admin: {
    name: 'Administración',
    icon: Settings,
    color: 'bg-gray-50 dark:bg-slate-9500',
    modules: [
      { id: 'mantenimiento', name: 'Mantenimiento', description: 'Órdenes de trabajo' },
      { id: 'configuracion', name: 'Configuración', description: 'Ajustes del condominio' },
    ]
  },
};

const ModuleConfigPage = () => {
  const { user } = useAuth();
  const [moduleConfig, setModuleConfig] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchModuleConfig();
  }, []);

  const fetchModuleConfig = async () => {
    if (!user?.condoId) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await moduleConfigAPI.getAll(user.condoId);
      const config: Record<string, boolean> = {};
      const mods = Array.isArray(response.data) ? response.data : (response.data?.data ?? []);
      mods.forEach((mod: ModuleConfig) => {
        config[mod.module_id] = mod.is_active;
      });
      
      // Set defaults for any missing modules
      Object.values(MODULE_CATEGORIES).forEach(category => {
        category.modules.forEach(mod => {
          if (config[mod.id] === undefined) {
            config[mod.id] = true; // Default to enabled
          }
        });
      });
      
      setModuleConfig(config);
    } catch (error: any) {
      console.error('Module config API error:', error);
      // Fallback to defaults if API not available
      const defaultConfig: Record<string, boolean> = {};
      Object.values(MODULE_CATEGORIES).forEach(category => {
        category.modules.forEach(mod => {
          defaultConfig[mod.id] = true;
        });
      });
      setModuleConfig(defaultConfig);
      toast.error('No se pudo cargar la configuración. Usando valores por defecto.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (moduleId: string) => {
    setModuleConfig(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const handleSave = async () => {
    if (!user?.condoId) {
      toast.error('Sin condominio asignado');
      return;
    }
    
    setSaving(true);
    try {
      // Send as array of { module_id, is_active } objects per spec
      const payload = Object.entries(moduleConfig).map(([moduleId, is_active]) => ({
        module_id: moduleId,
        is_active,
      }));
      await moduleConfigAPI.update(user.condoId, payload);
      localStorage.setItem('moduleConfig', JSON.stringify(moduleConfig));
      toast.success('Configuración guardada correctamente');
    } catch (error: any) {
      console.error('Save failed:', error);
      // Fallback: save to localStorage only
      localStorage.setItem('moduleConfig', JSON.stringify(moduleConfig));
      toast.error('Error al guardar en servidor. Cambios aplicados localmente.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-full bg-[#1a1a1a]">
        <Loader2 className="w-8 h-8 text-[#36CCD8] animate-spin" />
      </div>
    );
  }

  if (!user?.condoId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-full bg-[#1a1a1a] gap-4">
        <AlertCircle className="w-12 h-12 text-gray-500 dark:text-slate-400" />
        <p className="text-gray-300 text-lg">Sin condominio asignado</p>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#1a1a1a] p-6">
      <BackHeader fallback='/admin/condo' breadcrumb='Administración › Configuración de módulos' />
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[var(--v-text-primary)] mb-2">Configuración de Módulos</h1>
          <p className="text-gray-100">
            Activa o desactiva los módulos disponibles para tu condominio. 
            Los cambios afectarán inmediatamente a todos los usuarios.
          </p>
        </div>

        <div className="grid gap-6">
          {Object.entries(MODULE_CATEGORIES).map(([key, category]) => {
            const Icon = category.icon;
            return (
              <Card key={key} className="bg-[#2a2a2a] border-[#3a3a3a] shadow-lg">
                <CardHeader className="flex flex-row items-center gap-4">
                  <div className={`p-3 rounded-lg ${category.color}`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-xl text-white font-bold">{category.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {category.modules.map(module => (
                      <div 
                        key={module.id}
                        className="flex items-start justify-between p-4 rounded-lg bg-[#1a1a1a] border border-[#4a4a4a] hover:border-[#36CCD8] transition-colors"
                      >
                        <div className="flex-1 mr-4">
                          <h3 className="font-bold text-white mb-1">{module.name}</h3>
                          <p className="text-sm text-gray-100">{module.description}</p>
                        </div>
                        <Switch
                          checked={moduleConfig[module.id] ?? true}
                          onCheckedChange={() => handleToggle(module.id)}
                          className="data-[state=checked]:bg-[#36CCD8]"
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mt-8 flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#36CCD8] hover:bg-[#2DB8C4] text-black font-semibold px-8"
          >
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </div>

        <div className="mt-6 p-4 rounded-lg bg-yellow-500/20 border border-yellow-500/50">
          <h4 className="text-yellow-200 font-bold mb-2">⚠️ Nota importante</h4>
          <p className="text-sm text-gray-100">
            Los módulos desactivados no aparecerán en el menú lateral ni serán accesibles 
            por ningún usuario del condominio, incluyendo administradores. 
            Los módulos de Super Admin no pueden ser desactivados desde esta pantalla.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ModuleConfigPage;
